import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ITableCellModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { ICTTableCell, ICTTableCellProperties } from '@tencent/slide-sdk-model';
import { BorderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/border/border-converter';
import { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import { ITextBodyStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { TextBoxConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-box/text-box-converter';
import { emuToPt } from '@src/slide/infrastructure/module/independent/unit';

/**
 * 单元格
 */
export class TableCellPropertyConverter {
    /**
     * 单元格 sdk -> ui 属性映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTableCell>>,
        uiData: ITableCellModel,
    ): void {
        // 文本
        if (sdkData.txBody) {
            TextConverter.convertPartialDataFromSdkToUi(sdkData.txBody, uiData);
        }
        // 单元格文本框属性（文本的上下左右边距以及垂直对齐方向不在txBody中，而在tcPr上）
        if (sdkData.tcPr) {
            uiData.textBodyStyle = uiData.textBodyStyle || {};
            TableCellPropertyConverter.convertBoxSdkToUi(sdkData.tcPr, uiData.textBodyStyle);
        }
        // 填充
        if (sdkData.tcPr) {
            uiData.props.style.fillBackground = {};
            FillConverter.convertPartialDataFromSdkToUi(sdkData.tcPr, uiData.props.style.fillBackground);
        }
        // 边框
        TableCellPropertyConverter.convertBorderSdkToUi(sdkData, uiData);
        // 合并单元格
        TableCellPropertyConverter.convertMergeSdkToUi(sdkData, uiData);
    }

    /**
     * 单元格 ui -> sdk 属性映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<ITableCellModel>,
        sdkData: ICTTableCell,
    ): void {
        // 文本单独调接口处理，不在table的统一转换了
        // sdkData.txBody = sdkData.txBody || {};
        // TextConverter.convertPartialDataFromUiToSdk(uiData, sdkData.txBody);
        // 填充
        if (uiData.props?.style?.fillBackground) {
            sdkData.tcPr = sdkData.tcPr || {};
            FillConverter.convertPartialDataFromUiToSdk(uiData.props?.style?.fillBackground, sdkData.tcPr);
        }
        // 边框
        TableCellPropertyConverter.convertBorderUiToSdk(uiData, sdkData);
    }

    /**
     * 单元格文本框属性
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    private static convertBoxSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTableCellProperties>>,
        uiData: ITextBodyStyle,
    ): void {
        // 垂直对齐方式
        if (!uiData.wrapperStyle?.alignItems) {
            TextBoxConverter.convertAnchorSdkToUi(sdkData, uiData);
        }
        // 文字方向
        if (!uiData.wrapperStyle?.textDirection) {
            TextBoxConverter.convertVerticalTypeSdkToUi(sdkData, uiData);
        }
        // 上下左右边距
        uiData.bodyStyle = uiData.bodyStyle || {};
        if (uiData.bodyStyle.paddingLeft === undefined && (sdkData.marL || sdkData.marL === 0)) {
            uiData.bodyStyle.paddingLeft = emuToPt(sdkData.marL);
        }
        if (uiData.bodyStyle.paddingRight === undefined && (sdkData.marR || sdkData.marR === 0)) {
            uiData.bodyStyle.paddingRight = emuToPt(sdkData.marR);
        }
        if (uiData.bodyStyle.paddingTop === undefined && (sdkData.marT || sdkData.marT === 0)) {
            uiData.bodyStyle.paddingTop = emuToPt(sdkData.marT);
        }
        if (uiData.bodyStyle.paddingBottom === undefined && (sdkData.marB || sdkData.marB === 0)) {
            uiData.bodyStyle.paddingBottom = emuToPt(sdkData.marB);
        }
    }

    /**
     * 边框
     * @param sdkData
     * @param uiData
     */
    private static convertBorderSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTableCell>>,
        uiData: ITableCellModel,
    ): void {
        uiData.borders = {};
        if (sdkData.tcPr?.lnL) {
            const border: IBorderInfo = {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.tcPr.lnL, border);
            if (!border.noFill) {
                uiData.borders.left = { border };
            }
        }
        if (sdkData.tcPr?.lnR) {
            const border: IBorderInfo = {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.tcPr.lnR, border);
            if (!border.noFill) {
                uiData.borders.right = { border };
            }
        }
        if (sdkData.tcPr?.lnB) {
            const border: IBorderInfo = {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.tcPr.lnB, border);
            if (!border.noFill) {
                uiData.borders.bottom = { border };
            }
        }
        if (sdkData.tcPr?.lnT) {
            const border: IBorderInfo = {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.tcPr.lnT, border);
            if (!border.noFill) {
                uiData.borders.top = { border };
            }
        }
    }

    /**
     * 合并单元格
     * @param sdkData
     * @param uiData
     */
    private static convertMergeSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTableCell>>,
        uiData: ITableCellModel,
    ): void {
        uiData.rowspan = sdkData.rowSpan || 1;
        uiData.colspan = sdkData.gridSpan || 1;
        uiData.hMerge = !!sdkData.hMerge;
        uiData.vMerge = !!sdkData.vMerge;
    }

    /**
     * 边框
     * @param uiData
     * @param sdkData
     */
    private static convertBorderUiToSdk(
        uiData: DeepReadonly<DeepPartial<ITableCellModel>>,
        sdkData: ICTTableCell,
    ): void {
        sdkData.tcPr = sdkData.tcPr || {};
        if (uiData.borders?.left) {
            sdkData.tcPr.lnL = sdkData.tcPr.lnL || {};
            // 此处类型强转  因为上层调用少传了一层  后面可优化下
            BorderConverter.convertPartialDataFromUiToSdk(uiData.borders.left as IBorderInfo, sdkData.tcPr.lnL);
        }
        if (uiData.borders?.right) {
            sdkData.tcPr.lnR = sdkData.tcPr.lnR || {};
            // 此处类型强转  因为上层调用少传了一层  后面可优化下
            BorderConverter.convertPartialDataFromUiToSdk(uiData.borders.right as IBorderInfo, sdkData.tcPr.lnR);
        }
        if (uiData.borders?.bottom) {
            sdkData.tcPr.lnB = sdkData.tcPr.lnB || {};
            // 此处类型强转  因为上层调用少传了一层  后面可优化下
            BorderConverter.convertPartialDataFromUiToSdk(uiData.borders.bottom as IBorderInfo, sdkData.tcPr.lnB);
        }
        if (uiData.borders?.top) {
            sdkData.tcPr.lnT = sdkData.tcPr.lnT || {};
            // 此处类型强转  因为上层调用少传了一层  后面可优化下
            BorderConverter.convertPartialDataFromUiToSdk(uiData.borders.top as IBorderInfo, sdkData.tcPr.lnT);
        }
    }
}
